import axios from 'axios';
import rutas from '@/assets/utils/rutas.json';

const ESPECIALIDADES_REDACTORES = {
  IMAGENOLOGIA_CE: '5',
  ODONTOLOGIA_CE: '2'
};

const TIPOS_DOCTOR_PERMITIDOS = ['MEDICO IMAGEN', 'MEDICO ESPECIALISTA'];

const ALL_ESPECIALIDADES_QUERY = `
  query {
    allEspecialidad {
      id
      descripcion
      codEsp
    }
  }
`;

const IMAGENOLOGOS_QUERY = `
  query {
    allImagenologos {
      id
      idPersona{
        id
        primerNombre
        segundoNombre
        primerApellido
        segundoApellido
      }
    }
  }
`;

const DOCTORES_ESPECIALIDAD_QUERY = `
  query ($especialidad: ID) {
    allDoctores(especialidad: $especialidad) {
      status
      doctor {
        id
        tipomedico {
          id
          descripcion
        }
        especialidad {
          id
          descripcion
          codEsp
        }
        idPersona {
          id
          primerNombre
          segundoNombre
          primerApellido
          segundoApellido
        }
      }
    }
  }
`;

const normalizeText = (value = '') => value
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toUpperCase()
  .replace(/\s+/g, ' ')
  .trim();

const isEspecialidadPermitida = (especialidad = {}) => {
  const descripcion = normalizeText(especialidad?.descripcion);
  if (!descripcion) return false;

  const esImagenologiaCe = descripcion.includes('IMAGENOLOGIA') && descripcion.includes('CE');
  const esOdontologiaCe = descripcion.includes('ODONTOLOGIA') && descripcion.includes('CE');

  return esImagenologiaCe || esOdontologiaCe;
};

const isTipoDoctorPermitido = (tipomedico = {}) => {
  const descripcion = normalizeText(tipomedico?.descripcion);
  if (!descripcion) return false;

  return TIPOS_DOCTOR_PERMITIDOS.some(tipo => normalizeText(tipo) === descripcion);
};

const fetchImagenologosBase = async () => {
  try {
    const response = await axios.post(rutas.axios + 'api', { query: IMAGENOLOGOS_QUERY });
    const data = response?.data?.data?.allImagenologos;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('An error occurred while get imagenologos:', error);
    return [];
  }
};

const fetchDoctoresPorEspecialidad = async (especialidadId) => {
  if (!especialidadId) return [];

  try {
    const response = await axios.post(
      rutas.axios + 'api',
      { query: DOCTORES_ESPECIALIDAD_QUERY, variables: { especialidad: especialidadId } }
    );

    const doctoresData = response?.data?.data?.allDoctores;
    if (doctoresData?.status !== 'success') return [];

    return (doctoresData.doctor || []).map(doctor => ({
      id: doctor.id,
      idPersona: doctor.idPersona,
      especialidad: doctor.especialidad || null,
      tipomedico: doctor.tipomedico || null
    }));
  } catch (error) {
    console.error('An error occurred while fetching doctors by especialidad:', error);
    return [];
  }
};

const fetchEspecialidadesObjetivoIds = async () => {
  try {
    const response = await axios.post(rutas.axios + 'api', { query: ALL_ESPECIALIDADES_QUERY });
    const especialidades = response?.data?.data?.allEspecialidad || [];
    if (!Array.isArray(especialidades)) return [];

    const idsByDescripcion = especialidades
      .filter(esp => isEspecialidadPermitida(esp))
      .map(esp => esp.id)
      .filter(Boolean);

    const idsConFallback = [
      ...idsByDescripcion,
      ESPECIALIDADES_REDACTORES.IMAGENOLOGIA_CE,
      ESPECIALIDADES_REDACTORES.ODONTOLOGIA_CE
    ];

    return Array.from(new Set(idsConFallback.map(String)));
  } catch (error) {
    console.error('An error occurred while fetching especialidades:', error);
    return [
      ESPECIALIDADES_REDACTORES.IMAGENOLOGIA_CE,
      ESPECIALIDADES_REDACTORES.ODONTOLOGIA_CE
    ];
  }
};

const mergeDoctoresById = (...lists) => {
  const unique = new Map();

  lists.flat().forEach(doctor => {
    if (!doctor || !doctor.id) return;

    const previous = unique.get(doctor.id) || {};
    unique.set(doctor.id, {
      ...previous,
      ...doctor,
      idPersona: doctor.idPersona || previous.idPersona || null,
      especialidad: doctor.especialidad || previous.especialidad || null,
      tipomedico: doctor.tipomedico || previous.tipomedico || null
    });
  });

  return Array.from(unique.values());
};

const filterDoctoresPermitidos = (doctores = []) =>
  doctores.filter(doctor =>
    isTipoDoctorPermitido(doctor?.tipomedico) &&
    isEspecialidadPermitida(doctor?.especialidad)
  );

const getImagenologos = async () => {
  const [imagenologosBase, especialidadIds] = await Promise.all([
    fetchImagenologosBase(),
    fetchEspecialidadesObjetivoIds()
  ]);

  const doctoresByEspecialidad = await Promise.all(
    especialidadIds.map(id => fetchDoctoresPorEspecialidad(id))
  );

  const doctoresFiltrados = filterDoctoresPermitidos(doctoresByEspecialidad.flat());
  const doctores = mergeDoctoresById(imagenologosBase, doctoresFiltrados);
  return doctores.filter(doctor => !!doctor?.idPersona);
};

const getImagenologosSelect = async () => {
  const imagenologos = await getImagenologos();

  const imagenologosOpts = imagenologos.map(item => {
    const persona = item.idPersona || {};
    const nameParts = [
      persona.primerNombre,
      persona.segundoNombre,
      persona.primerApellido,
      persona.segundoApellido
    ].filter(Boolean);

    return {
      value: item.id,
      text: nameParts.join(' ')
    };
  });

  const uniqueMap = new Map();
  imagenologosOpts.forEach(item => {
    if (!item?.value) return;
    if (!uniqueMap.has(item.value)) uniqueMap.set(item.value, item);
  });

  return Array.from(uniqueMap.values())
    .sort((a, b) => a.text.localeCompare(b.text, 'es', { sensitivity: 'base' }));
};

export const imagenologosService = {
  getImagenologos,
  getImagenologosSelect
};
